import React, { ReactElement } from 'react'
import cn from 'classnames'
import Image from '@corratech/pylot-image'
import { useTranslation } from 'next-i18next/pages'
import type { Maybe } from '@pylot-data/pylotschema'
import s from './VideoPlayer.module.scss'

type VideoPlayerPropType = {
    alt?: string
    url: string
    index: number
    image?: Maybe<string>
    label?: Maybe<string>
    isThumbnail?: boolean
}

const VideoPlayer = ({
    alt,
    url,
    index,
    image,
    label,
    isThumbnail
}: VideoPlayerPropType): ReactElement => {
    const { t } = useTranslation(['common'])
    const getId = (urlForID: string) => {
        const regExp =
            /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
        const match = urlForID.match(regExp)

        return match && match[2].length === 11 ? match[2] : null
    }

    const youtubeRegExp = new RegExp(
        '^(?:https?://|//)?(?:www\\.|m\\.)?' +
            '(?:youtu\\.be/|youtube\\.com/(?:embed/|v/|watch\\?v=|watch\\?.+&v=))([\\w-]{11})(?![\\w-])'
    )
    const vimeoRegExp = new RegExp(
        'https?://(?:www\\.|player\\.)?vimeo.com/(?:channels/' +
            '(?:\\w+/)?|groups/([^/]*)/videos/|album/(\\d+)/video/|video/|)(\\d+)(?:$|/|\\?)'
    )

    const makeUrl = (urlValue: string) => {
        const commonParams =
            'loop=0&autoplay=0&controls=1&rel=0&showinfo=0&autohide=1'

        if (youtubeRegExp.test(urlValue)) {
            return `${urlValue}?playlist=${getId(
                urlValue
            )}&mute=1&${commonParams}`
        } else if (vimeoRegExp.test(urlValue)) {
            return `${urlValue}?background=1&muted=1&${commonParams}`
        }
        return ''
    }

    if (isThumbnail) {
        return (
            <Image
                src={image ?? ''}
                alt={alt || label || t('Product Thumbnail')}
                width={100}
                height={100}
                quality="85"
            />
        )
    }

    return (
        <iframe
            title={url}
            allow="autoplay"
            className={cn(s['video-iframe'], index % 3 === 0 ? s['big'] : '')}
            frameBorder="0"
            src={makeUrl(url)}
        />
    )
}

export default VideoPlayer
